import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL, errorMessageFromBody, isNetworkError, NETWORK_ERROR_MESSAGE } from '../../../services/api';
import { isTrustedOAuthMessage } from '../../../utils/oauthMessage';
import { DataFreshnessBanner, PREPARING_POLL, PreparingNotice, useBackgroundRefresh } from './DataFreshness';

const MAX_CHART_VAL = 5;

// --- TypeScript interfaces ---

interface PlatformSentiment {
  positive: number;
  neutral: number;
  negative: number;
}

interface Platform {
  name: string;
  icon: string;
  followers: number | null;
  engagementRate: number;
  postFrequency: number;
  healthScore: number;
  trend: 'up' | 'down' | 'stable' | null;
  trendValue: number;
  status: 'strong' | 'weak';
  lastPost: string | null;
  sentiment: PlatformSentiment | null;
  formatDiversity: number;
  color: string;
  needsReconnect?: boolean;
  reconnectMessage?: string | null;
  profileUrl?: string;
}

interface EngagementEntry {
  month: string;
  [key: string]: number | string;
}

interface PresenceSummary {
  avgHealthScore: number;
  avgHealthScorePreviousPeriod: number | null;
  baselineEngagement: number;
  baselineEngagementPreviousPeriod: number | null;
  activePlatformCount: number;
  strongPlatformCount: number;
  weakPlatformCount: number;
  lastActivityAt: string | null;
}

interface SocialPresenceData {
  summary: PresenceSummary;
  platforms: Platform[];
  engagementHistory: EngagementEntry[];
  updatedAt?: string | null;
  refreshing?: boolean;
  preparing?: boolean;
}

// --- Inline helper: relative time ---

function formatRelativeTime(iso: string | null | undefined): string {
  if (!iso) return 'No activity recorded';
  let normalizedIso = iso;
  if (typeof normalizedIso === 'string') {
    normalizedIso = normalizedIso.replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
  }
  // Calendar days, not 24-hour blocks: a post yesterday afternoon is "Yesterday", not "Today".
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diffDays = Math.round((startOfDay(new Date()) - startOfDay(new Date(normalizedIso))) / 86400000);
  if (diffDays <= 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  }
  if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return `${months} month${months > 1 ? 's' : ''} ago`;
  }
  const years = Math.floor(diffDays / 365);
  return `${years} year${years > 1 ? 's' : ''} ago`;
}

function Sparkline({ data, color }: { data: number[]; color: string }) {
  const w = 120;
  const h = 40;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const step = w / (data.length - 1);
  const points = data.map((v, i) => {
    const x = i * step;
    const y = h - ((v - min) / range) * (h - 4) - 2;
    return `${x},${y}`;
  });
  const path = `M ${points.join(' L ')}`;
  return (
    <svg width={w} height={h} className="overflow-visible">
      <path d={path} fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" />
      {data.map((v, i) => {
        const x = i * step;
        const y = h - ((v - min) / range) * (h - 4) - 2;
        return <circle key={i} cx={x} cy={y} r={2.5} fill={color} />;
      })}
    </svg>
  );
}

function EngagementBarChart({
  platforms,
  engagementHistory,
}: {
  platforms: Platform[];
  engagementHistory: EngagementEntry[];
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [tooltip, setTooltip] = useState<{
    show: boolean;
    x: number;
    y: number;
    month: string;
    platform: string;
    value: number;
  }>({ show: false, x: 0, y: 0, month: '', platform: '', value: 0 });

  useEffect(() => {
    function update() {
      if (containerRef.current) {
        setWidth(containerRef.current.clientWidth);
      }
    }
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  if (!engagementHistory || engagementHistory.length === 0) {
    return (
      <p className="text-sm text-gray-500 py-6 text-center">
        Engagement history will appear once data has been collected.
      </p>
    );
  }

  const platformKeys = platforms.map((p) => p.name.toLowerCase());
  const labels = engagementHistory.map((h) => h.month);
  const colors: Record<string, string> = platforms.reduce(
    (acc, p) => ({ ...acc, [p.name.toLowerCase()]: p.color }),
    {} as Record<string, string>
  );
  const platformNames: Record<string, string> = platforms.reduce(
    (acc, p) => ({ ...acc, [p.name.toLowerCase()]: p.name }),
    {} as Record<string, string>
  );
  const gap = 6;
  const chartH = 140;
  const totalW = width > 0 ? width : labels.length * (platformKeys.length * 18 + gap) + gap;
  const barW = width > 0 ? (totalW - gap * (labels.length + 1)) / (labels.length * platformKeys.length) : 18;

  return (
    <div ref={containerRef} className="w-full relative">
      <svg width={totalW} height={chartH + 40}>
        {labels.map((label, li) => {
          const groupX = li * (platformKeys.length * barW + gap) + gap;
          return (
            <g key={label}>
              {platformKeys.map((p, pi) => {
                const val = (engagementHistory[li][p] as number) || 0;
                const barH = (val / MAX_CHART_VAL) * chartH;
                const x = groupX + pi * barW;
                const y = chartH - barH;
                return (
                  <rect
                    key={p}
                    x={x}
                    y={y}
                    width={barW - 2}
                    height={barH}
                    rx={3}
                    fill={colors[p]}
                    opacity={0.85}
                    onMouseEnter={(e) => {
                      const rect = containerRef.current?.getBoundingClientRect();
                      if (rect) {
                        setTooltip({
                          show: true,
                          x: e.clientX - rect.left,
                          y: e.clientY - rect.top,
                          month: label,
                          platform: platformNames[p],
                          value: val,
                        });
                      }
                    }}
                    onMouseMove={(e) => {
                      const rect = containerRef.current?.getBoundingClientRect();
                      if (rect) {
                        setTooltip((prev) => ({
                          ...prev,
                          x: e.clientX - rect.left,
                          y: e.clientY - rect.top,
                        }));
                      }
                    }}
                    onMouseLeave={() =>
                      setTooltip((prev) => ({ ...prev, show: false }))
                    }
                    style={{ cursor: 'pointer' }}
                  />
                );
              })}
              <text x={groupX + (platformKeys.length * barW) / 2} y={chartH + 20} textAnchor="middle" fontSize="12" fill="#6B7280">
                {label}
              </text>
            </g>
          );
        })}
      </svg>

      {tooltip.show && (
        <div
          className="absolute pointer-events-none bg-gray-900 text-white text-xs rounded-lg px-2.5 py-1.5 z-10 shadow-lg whitespace-nowrap"
          style={{
            left: tooltip.x + 10,
            top: tooltip.y - 36,
          }}
        >
          <div className="font-semibold">{tooltip.platform}</div>
          <div className="text-gray-300">
            {tooltip.month}: <span className="text-white font-bold">{tooltip.value}%</span> engagement
          </div>
        </div>
      )}
    </div>
  );
}

export default function SocialPresenceOverview() {
  const navigate = useNavigate();
  const [selectedPeriod, setSelectedPeriod] = useState('Monthly');
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [presenceData, setPresenceData] = useState<SocialPresenceData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [connectingId, setConnectingId] = useState<string | null>(null);
  // Set while the OAuth code is being exchanged (after the popup has closed),
  // so the button keeps its spinner until the reconnect really finishes.
  const [exchangingId, setExchangingId] = useState<string | null>(null);
  const [reconnectNotice, setReconnectNotice] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const popupRef = useRef<Window | null>(null);

  // Success notices clear themselves after a few seconds.
  useEffect(() => {
    if (reconnectNotice?.type !== 'success') return;
    const t = setTimeout(() => setReconnectNotice(null), 5000);
    return () => clearTimeout(t);
  }, [reconnectNotice]);

  // Add OAuth message listener
  useEffect(() => {
    const handler = async (event: MessageEvent) => {
      if (!isTrustedOAuthMessage(event)) return;
      if (event.data?.type === 'SMM_OAUTH_CODE') {
        const platform = event.data.platform;
        const code = event.data.code;
        if (platform && code) {
          await handleReconnectCode(platform, code);
        }
      } else if (event.data?.type === 'SMM_OAUTH_ERROR') {
        setConnectingId(null);
        alert(`Connection failed: ${event.data.error || 'Failed to authenticate platform'}`);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [presenceData, selectedPeriod]);

  const handleReconnectClick = async (platformName: string) => {
    const userId = localStorage.getItem('user_id');
    if (!userId) return;

    setConnectingId(platformName);
    const token = localStorage.getItem('access_token');

    try {
      const res = await fetch(`${API_BASE_URL}/smm/onboarding/oauth/url?platform=${platformName}&user_id=${userId}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errorMessageFromBody(errBody, res.status));
      }
      const data = await res.json();

      if (data && data.authorization_url) {
        const popup = window.open(
          data.authorization_url,
          `${platformName}_oauth`,
          'width=600,height=700,left=100,top=100'
        );
        if (!popup) {
          setConnectingId(null);
          alert('Your browser blocked the sign-in window. Allow pop-ups for this site and try again.');
          return;
        }
        popupRef.current = popup;

        const poll = setInterval(() => {
          if (!popup || popup.closed) {
            clearInterval(poll);
            setConnectingId((prev) => (prev === platformName ? null : prev));
          }
        }, 500);
      } else {
        throw new Error('No authorization URL returned');
      }
    } catch (err: any) {
      setConnectingId(null);
      alert(isNetworkError(err) ? NETWORK_ERROR_MESSAGE : err?.message || 'Failed to initiate reconnection');
    }
  };

  const handleReconnectCode = async (platformName: string, code: string) => {
    const userId = localStorage.getItem('user_id');
    if (!userId) return;
    const token = localStorage.getItem('access_token');

    const platformObj = presenceData?.platforms.find(p => p.name.toLowerCase() === platformName.toLowerCase());
    const profileUrl = platformObj?.profileUrl || '';
    const platformLabel = platformName.charAt(0).toUpperCase() + platformName.slice(1);

    setExchangingId(platformName);
    setReconnectNotice(null);
    try {
      const response = await fetch(`${API_BASE_URL}/smm/onboarding/step1`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          user_id: userId,
          platforms: [
            {
              platform: platformName.toLowerCase(),
              profile_url: profileUrl,
              oauth_code: code,
              connected: true
            }
          ],
          permissions: {
            reach_analytics: true,
            audience_demographics: true,
            comment_visibility: true,
            watch_time_subscriptions: true
          }
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errorMessageFromBody(errData, response.status));
      }
    } catch (err: any) {
      setExchangingId(null);
      setConnectingId(null);
      alert(isNetworkError(err) ? NETWORK_ERROR_MESSAGE : err?.message || 'Failed to reconnect channel');
      return;
    }

    // Reconnection successful! Force refresh of presence data
    try {
      const period = selectedPeriod.toLowerCase();
      const refreshRes = await fetch(`${API_BASE_URL}/smm/strategy/social-presence/${userId}?period=${period}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!refreshRes.ok) {
        const errData = await refreshRes.json().catch(() => ({}));
        throw new Error(errorMessageFromBody(errData, refreshRes.status));
      }
      const newData: SocialPresenceData = await refreshRes.json();
      setPresenceData(newData);
      // Only report success if the refreshed data no longer flags the platform.
      const stillBroken = newData?.platforms?.find(
        (p) => p.name.toLowerCase() === platformName.toLowerCase() && p.needsReconnect
      );
      setReconnectNotice(
        stillBroken
          ? { type: 'error', text: stillBroken.reconnectMessage || `${platformLabel} still needs to be reconnected. Please try again.` }
          : { type: 'success', text: `${platformLabel} reconnected.` }
      );
    } catch (err: any) {
      const reason = isNetworkError(err) ? NETWORK_ERROR_MESSAGE : err?.message;
      setReconnectNotice({
        type: 'error',
        text: `${platformLabel} was reconnected, but the latest data couldn't be loaded. Refresh the page to see it.${reason ? ` ${reason}` : ''}`,
      });
    } finally {
      setExchangingId(null);
      setConnectingId(null);
    }
  };

  useEffect(() => {
    const userId = localStorage.getItem('user_id');
    if (!userId) {
      setFetchError('User not authenticated');
      setIsLoading(false);
      return;
    }
    const token = localStorage.getItem('access_token');
    const period = selectedPeriod.toLowerCase();
    setIsLoading(true);
    setFetchError(null);
    fetch(`${API_BASE_URL}/smm/strategy/social-presence/${userId}?period=${period}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    })
      .then(async (res) => {
        if (!res.ok) {
          const errBody = await res.json().catch(() => ({}));
          throw new Error(errorMessageFromBody(errBody, res.status));
        }
        return res.json();
      })
      .then((data: SocialPresenceData) => {
        setPresenceData(data);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load social presence data:', err);
        setFetchError(isNetworkError(err) ? NETWORK_ERROR_MESSAGE : err?.message || 'Failed to load social presence data.');
        setIsLoading(false);
      });
  }, [selectedPeriod, reloadKey]);

  // Re-ask quietly (no skeleton) and swap the answer in. refresh=true is the
  // Refresh button: the server starts fetching newer numbers in the background.
  const refetchQuietly = async (refresh = false) => {
    const userId = localStorage.getItem('user_id');
    const token = localStorage.getItem('access_token');
    const res = await fetch(
      `${API_BASE_URL}/smm/strategy/social-presence/${userId}?period=${selectedPeriod.toLowerCase()}${refresh ? '&refresh=true' : ''}`,
      {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      },
    );
    if (res.ok) setPresenceData(await res.json());
  };
  // The stored report is shown at once; while the server fetches newer
  // numbers (or prepares the first report) keep asking and swap them in.
  const freshness = useBackgroundRefresh(presenceData?.refreshing, () => refetchQuietly());
  const preparing = useBackgroundRefresh(presenceData?.preparing, () => refetchQuietly(), PREPARING_POLL);

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        {/* Summary bar skeleton */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="h-4 bg-gray-200 rounded-md w-44" />
            <div className="h-8 bg-gray-100 rounded-lg w-28" />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                <div className="h-3 bg-gray-200 rounded w-3/4 mb-3" />
                <div className="h-7 bg-gray-200 rounded w-1/2 mb-2" />
                <div className="h-2 bg-gray-100 rounded w-full" />
              </div>
            ))}
          </div>
        </div>
        {/* Platform cards skeleton — 2×2 grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-200 rounded-xl" />
                  <div>
                    <div className="h-4 bg-gray-200 rounded w-24 mb-1.5" />
                    <div className="h-3 bg-gray-100 rounded w-16" />
                  </div>
                </div>
                <div className="h-6 bg-gray-200 rounded-full w-16" />
              </div>
              <div className="grid grid-cols-3 gap-3 mb-4">
                {[...Array(3)].map((_, j) => (
                  <div key={j} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                    <div className="h-2.5 bg-gray-200 rounded w-2/3 mb-2" />
                    <div className="h-5 bg-gray-200 rounded w-1/2" />
                  </div>
                ))}
              </div>
              <div className="h-2 bg-gray-100 rounded-full w-full">
                <div className="h-2 bg-gray-200 rounded-full" style={{ width: `${40 + i * 12}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (fetchError || !presenceData) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
        <p className="font-semibold">Failed to load social presence data.</p>
        {fetchError && <p className="mt-1">{fetchError}</p>}
        <button
          onClick={() => setReloadKey((k) => k + 1)}
          className="mt-3 px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-800 rounded-lg text-xs font-semibold transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  if (presenceData.preparing) {
    return <PreparingNotice state={preparing} onRetry={() => setReloadKey((k) => k + 1)} />;
  }

  if (presenceData.summary.activePlatformCount === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[500px] bg-white border border-slate-200 rounded-2xl p-10 text-center shadow-sm relative overflow-hidden">
        {/* Background glows */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 pointer-events-none" />
        <div className="absolute -top-20 -left-20 w-56 h-56 rounded-full bg-blue-50 blur-3xl opacity-60" />
        <div className="absolute -bottom-20 -right-20 w-56 h-56 rounded-full bg-indigo-50 blur-3xl opacity-60" />

        {/* Icon */}
        <div className="relative mb-6 z-10">
          <div className="w-24 h-24 bg-gradient-to-br from-slate-100 to-blue-50 border border-slate-200 rounded-3xl flex items-center justify-center shadow-inner">
            <i className="ri-global-line text-5xl text-blue-400"></i>
          </div>
          <div className="absolute -bottom-2 -right-2 w-9 h-9 bg-blue-600 rounded-2xl flex items-center justify-center shadow-lg border-2 border-white">
            <i className="ri-link text-white text-base"></i>
          </div>
        </div>

        {/* Headline */}
        <h3 className="text-2xl font-bold text-gray-900 mb-2 relative z-10">No Channels Connected</h3>
        <p className="text-sm text-gray-500 max-w-sm mb-8 leading-relaxed relative z-10">
          Connect your social media accounts in the profile section to start tracking platform health, engagement trends, and audience insights across all your channels.
        </p>

        {/* Platform pill hints */}
        <div className="flex flex-wrap justify-center gap-2 mb-8 relative z-10">
          {[
            { icon: 'ri-instagram-line', label: 'Instagram', color: 'from-pink-50 to-orange-50 border-pink-100 text-pink-600' },
            { icon: 'ri-facebook-circle-line', label: 'Facebook', color: 'from-blue-50 to-indigo-50 border-blue-100 text-blue-600' },
            { icon: 'ri-youtube-line', label: 'YouTube', color: 'from-red-50 to-rose-50 border-red-100 text-red-500' },
            { icon: 'ri-linkedin-box-line', label: 'LinkedIn', color: 'from-sky-50 to-blue-50 border-sky-100 text-sky-700' },
          ].map((p) => (
            <div
              key={p.label}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r ${p.color} border text-xs font-semibold`}
            >
              <i className={`${p.icon} text-sm`}></i>
              {p.label}
            </div>
          ))}
        </div>

        {/* CTA */}
        {/* <button
          onClick={() => navigate('/content-onboarding')}
          className="inline-flex items-center gap-2 px-6 py-3 bg-[#0859B8] hover:bg-[#074a9b] text-white rounded-xl text-sm font-bold shadow-md hover:shadow-lg transition-all relative z-10 cursor-pointer"
        >
          <i className="ri-add-line text-base"></i>
          Connect Your First Channel
        </button> */}
      </div>
    );
  }

  if (import.meta.env.VITE_ENABLE_ANALYTICS !== 'true') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[500px] bg-white border border-slate-200 rounded-2xl p-8 text-center shadow-sm relative overflow-hidden">
        {/* Background patterns */}
        <div className="absolute inset-0 bg-gradient-to-tr from-slate-50 via-white to-blue-50/20 opacity-70 pointer-events-none" />
        <div className="absolute -top-24 -left-24 w-48 h-48 rounded-full bg-blue-50 blur-3xl opacity-50" />
        <div className="absolute -bottom-24 -right-24 w-48 h-48 rounded-full bg-teal-50 blur-3xl opacity-50" />

        {/* Icon wrapper */}
        <div className="relative mb-6 z-10">
          <div className="w-20 h-20 bg-slate-50 border border-slate-100 rounded-2xl flex items-center justify-center shadow-inner animate-pulse">
            <i className="ri-global-line text-4xl text-blue-600"></i>
          </div>
          <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-blue-600 rounded-xl flex items-center justify-center shadow-lg border-2 border-white">
            <i className="ri-time-line text-white text-sm"></i>
          </div>
        </div>

        {/* Text */}
        <h3 className="text-2xl font-bold text-gray-900 mb-2 relative z-10">Social Presence Overview</h3>
        <p className="text-sm text-gray-500 max-w-md mb-8 leading-relaxed relative z-10">
          Evaluate your brand's social media maturity, audience health, and platform-specific performance. This feature is currently under active development.
        </p>

        {/* Premium Badge */}
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50/70 border border-blue-100 text-xs font-semibold text-blue-700 relative z-10">
          <i className="ri-compass-3-line"></i>
          Platform Integration Underway
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 w-full">
      <DataFreshnessBanner updatedAt={presenceData.updatedAt} state={freshness} onRefresh={() => refetchQuietly(true)} />

      {/* Header Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Overall Health Score</p>
          <div className="flex items-end gap-2">
            <span className="text-3xl font-bold text-gray-900">{presenceData.summary.avgHealthScore}%</span>
            {presenceData.summary.avgHealthScorePreviousPeriod != null && (() => {
              const delta = presenceData.summary.avgHealthScore - presenceData.summary.avgHealthScorePreviousPeriod!;
              return (
                <span className={`text-sm font-medium mb-1 ${delta >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {delta >= 0 ? '+' : ''}{delta}% vs last period
                </span>
              );
            })()}
          </div>
          <div className="mt-3 w-full bg-gray-100 rounded-full h-2">
            <div className="bg-emerald-500 h-2 rounded-full" style={{ width: `${presenceData.summary.avgHealthScore}%` }} />
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Baseline Engagement</p>
          <div className="flex items-end gap-2">
            <span className="text-3xl font-bold text-gray-900">{presenceData.summary.baselineEngagement}%</span>
            {presenceData.summary.baselineEngagementPreviousPeriod != null && (() => {
              const delta = Number(
                (presenceData.summary.baselineEngagement - presenceData.summary.baselineEngagementPreviousPeriod!).toFixed(1)
              );
              return (
                <span className={`text-sm font-medium mb-1 ${delta >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {delta >= 0 ? '+' : ''}{delta}% vs last period
                </span>
              );
            })()}
          </div>
          <p className="text-xs text-gray-500 mt-2">Across all active platforms</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Active Platforms</p>
          <div className="flex items-end gap-2">
            <span className="text-3xl font-bold text-gray-900">{presenceData.summary.activePlatformCount}</span>
            <span className="text-sm font-medium text-gray-600 mb-1">
              {presenceData.summary.strongPlatformCount} strong / {presenceData.summary.weakPlatformCount} need attention
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {presenceData.summary.lastActivityAt
              ? `Last activity tracked ${formatRelativeTime(presenceData.summary.lastActivityAt)}`
              : 'No activity recorded'}
          </p>
        </div>
      </div>

      {/* Period Toggle */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-gray-900">Platform Health Dashboard</h3>
        <div className="flex bg-white border border-gray-200 rounded-lg overflow-hidden">
          <span className="px-4 py-2 text-sm font-medium bg-gray-900 text-white whitespace-nowrap">
            Monthly
          </span>
        </div>
      </div>

      {/* Imbalance Detection Banner */}
      {(() => {
        const activeCount = presenceData.platforms.filter((p) => p.status === 'strong' || p.postFrequency >= 4).length;
        const inactiveCount = presenceData.platforms.filter((p) => p.status === 'weak' || p.postFrequency < 3).length;
        if (!bannerDismissed && activeCount > 0 && inactiveCount > 0) {
          const activeNames = presenceData.platforms
            .filter((p) => p.status === 'strong' || p.postFrequency >= 4)
            .map((p) => p.name)
            .join(', ');
          const inactiveNames = presenceData.platforms
            .filter((p) => p.status === 'weak' || p.postFrequency < 3)
            .map((p) => p.name)
            .join(', ');
          return (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-amber-100 flex-shrink-0 mt-0.5">
                <i className="ri-alert-line text-amber-600"></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-amber-800">Channel imbalance detected</p>
                <p className="text-xs text-amber-700">
                  Active: {activeNames} — Inactive: {inactiveNames}. Consider redistributing content effort.
                </p>
              </div>
              <button
                onClick={() => setBannerDismissed(true)}
                className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-amber-100 text-amber-600 transition-colors flex-shrink-0"
                aria-label="Dismiss"
              >
                <i className="ri-close-line text-sm"></i>
              </button>
            </div>
          );
        }
        return null;
      })()}

      {/* Reconnect result */}
      {reconnectNotice && (
        <div
          className={`rounded-lg px-4 py-3 flex items-center justify-between gap-3 border ${
            reconnectNotice.type === 'success'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : 'bg-amber-50 border-amber-200 text-amber-800'
          }`}
        >
          <div className="flex items-center gap-2">
            <i className={reconnectNotice.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'}></i>
            <p className="text-sm">{reconnectNotice.text}</p>
          </div>
          <button onClick={() => setReconnectNotice(null)} className="opacity-60 hover:opacity-100" aria-label="Dismiss">
            <i className="ri-close-line text-sm"></i>
          </button>
        </div>
      )}

      {/* Platform Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4">
        {presenceData.platforms.map((platform) => {
          if (platform.needsReconnect) {
            return (
              <div key={platform.name} className="bg-slate-50 rounded-xl border border-amber-200/80 p-5 shadow-sm hover:shadow-md transition-shadow flex flex-col relative overflow-hidden min-h-[300px]">
                {/* Visual Premium Gradient Background Accent */}
                <div className="absolute top-0 right-0 w-24 h-24 bg-amber-100/50 rounded-full blur-2xl pointer-events-none" />

                {/* Header */}
                <div className="flex items-center justify-between mb-4 relative z-10">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-white border border-amber-100 shadow-sm">
                      <i className={`${platform.icon} text-xl text-amber-600`}></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 text-sm">{platform.name.charAt(0).toUpperCase() + platform.name.slice(1)}</h4>
                      <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-100 px-1.5 py-0.5 rounded-full uppercase tracking-wider">
                        Reconnection Needed
                      </span>
                    </div>
                  </div>
                </div>

                {/* Content Message */}
                <div className="my-auto py-4 relative z-10 flex flex-col items-center text-center">
                  <div className="w-12 h-12 bg-amber-50 border border-amber-100 rounded-full flex items-center justify-center mb-3">
                    <i className="ri-error-warning-line text-amber-600 text-xl"></i>
                  </div>
                  <h5 className="font-bold text-gray-900 text-sm mb-1">
                    {platform.reconnectMessage ? 'Page Not Accessible' : 'Connection Expired'}
                  </h5>
                  <p className="text-xs text-gray-500 max-w-xs leading-relaxed">
                    {platform.reconnectMessage ||
                      `Your ${platform.name.charAt(0).toUpperCase() + platform.name.slice(1)} authentication has expired or been invalidated. Reconnect to resume tracking metrics and audience sentiment.`}
                  </p>
                </div>

                {/* Reconnect Button */}
                <div className="mt-auto pt-4 border-t border-slate-200/60 relative z-10 flex items-center justify-between">
                  <span className="text-[11px] text-gray-400 font-medium">Last active: Unknown</span>
                  <button
                    onClick={() => handleReconnectClick(platform.name.toLowerCase())}
                    disabled={connectingId !== null || exchangingId !== null}
                    className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-[#0859B8] hover:bg-[#074a9b] disabled:opacity-50 text-white rounded-lg text-xs font-bold shadow-sm transition-all hover:shadow cursor-pointer"
                  >
                    {exchangingId === platform.name.toLowerCase() ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Reconnecting...
                      </>
                    ) : connectingId === platform.name.toLowerCase() ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Connecting...
                      </>
                    ) : (
                      <>
                        <i className="ri-refresh-line"></i>
                        Reconnect Platform
                      </>
                    )}
                  </button>
                </div>
              </div>
            );
          }

          const activityLevel = platform.postFrequency >= 15 ? 'High' : platform.postFrequency >= 6 ? 'Medium' : 'Low';
          const activityColor = platform.postFrequency >= 15 ? 'bg-emerald-50 text-emerald-700' : platform.postFrequency >= 6 ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700';

          const contentHealthIndex = platform.healthScore;

          // null when there were no comments or captions to analyse
          const s = platform.sentiment;

          // Imbalance flag for this card
          const isImbalanced = (platform.status === 'weak' || platform.postFrequency < 3) &&
            presenceData.platforms.some((p) => p.status === 'strong' || p.postFrequency >= 4);

          return (
            <div key={platform.name} className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow flex flex-col">
              {/* 1. Platform Activity Score */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-gray-50">
                    <i className={`${platform.icon} text-xl text-gray-700`}></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 text-sm">{platform.name}</h4>
                    <p className="text-xs text-gray-500">
                      {platform.followers != null ? `${platform.followers.toLocaleString()} followers` : 'Followers not available'}
                    </p>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-bold ${activityColor}`}>
                  {activityLevel} activity
                </span>
              </div>

              {/* 2. Content Health Index */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-gray-700">Content Health Index</span>
                  <span className="text-xs font-bold text-gray-900">{contentHealthIndex}</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-1.5">
                  <div
                    className={`h-1.5 rounded-full ${contentHealthIndex >= 70 ? 'bg-emerald-500' : contentHealthIndex >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                    style={{ width: `${contentHealthIndex}%` }}
                  />
                </div>
              </div>

              {/* 4. Sentiment Snapshot */}
              <div className="mb-3">
                <p className="text-xs font-semibold text-gray-700 mb-2">Sentiment Snapshot</p>
                {s ? (
                  <div className="flex items-center gap-4">
                    <span className="text-xs text-emerald-600 font-bold">{s.positive}% <span className="font-normal text-gray-500">Positive</span></span>
                    <span className="text-xs text-gray-500 font-bold">{s.neutral}% <span className="font-normal text-gray-500">Neutral</span></span>
                    <span className="text-xs text-red-600 font-bold">{s.negative}% <span className="font-normal text-gray-500">Negative</span></span>
                  </div>
                ) : (
                  <p className="text-xs text-gray-400">No sentiment data — no comments or captions to analyse yet.</p>
                )}
              </div>

              {/* 3. Platform Imbalance Detection */}
              {isImbalanced && (
                <div className="mb-3 flex items-center gap-2 bg-red-50 border border-red-100 rounded-lg p-2">
                  <i className="ri-error-warning-line text-red-500 text-sm"></i>
                  <span className="text-xs text-red-700 font-medium">Channel imbalance detected</span>
                </div>
              )}

              {/* Bottom meta */}
              <div className="mt-auto pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                {platform.trend === 'up' || platform.trend === 'down' ? (
                  <span className={platform.trend === 'up' ? 'text-emerald-600 font-medium' : 'text-red-600 font-medium'}>
                    {platform.trend === 'up' ? '+' : '-'}{platform.trendValue}% trend
                  </span>
                ) : (
                  <span className="text-gray-500 font-medium">Stable</span>
                )}
                <span>Last post: {platform.lastPost ? formatRelativeTime(platform.lastPost) : 'No posts yet'}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Weak / Strong Identification */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch">
        <div className="bg-white rounded-xl border border-gray-200 p-5 flex flex-col h-full">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
            <h4 className="font-semibold text-gray-900">Strongest Platforms</h4>
          </div>
          <div className="space-y-3 flex-1">
            {presenceData.platforms.filter((p) => p.status === 'strong').map((p) => (
              <div key={p.name} className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <i className={`${p.icon} text-lg text-emerald-600`}></i>
                  <span className="text-sm font-medium text-gray-900">{p.name}</span>
                </div>
                <span className="text-sm font-bold text-emerald-700">{p.healthScore}% health</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5 flex flex-col h-full">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-2 h-2 rounded-full bg-red-500"></div>
            <h4 className="font-semibold text-gray-900">Needs Attention</h4>
          </div>
          <div className="space-y-3 flex-1">
            {presenceData.platforms.filter((p) => p.status === 'weak').map((p) => (
              <div key={p.name} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <i className={`${p.icon} text-lg text-red-600`}></i>
                  <span className="text-sm font-medium text-gray-900">{p.name}</span>
                </div>
                <span className="text-sm font-bold text-red-700">{p.healthScore}% health</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-3">Consider increasing post frequency or testing new content formats.</p>
        </div>
      </div>

      {/* Engagement Trend Chart */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h4 className="font-semibold text-gray-900 mb-4">Engagement Rate Trends ({selectedPeriod})</h4>
        {!presenceData.engagementHistory || presenceData.engagementHistory.length === 0 ? (
          <p className="text-sm text-gray-500 py-6 text-center">
            Engagement history will appear once data has been collected.
          </p>
        ) : (
          <>
            <EngagementBarChart platforms={presenceData.platforms} engagementHistory={presenceData.engagementHistory} />
            <div className="flex flex-wrap gap-4 mt-4">
              {presenceData.platforms.map((p) => (
                <div key={p.name} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ background: p.color }}></div>
                  <span className="text-xs text-gray-600">{p.name}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
